import { useState, useEffect } from 'react';
import HeaderTab from './Pages/CommonComponents/HeaderTab';
import HomePage from './Pages/HomePage/HomePage';
import { init } from './api/axios';
import { CandidateService } from './Services/CandidateService';
import FullPageCenteredSpinner from './Pages/CommonComponents/FullPageCenteredSpinner';
import { withAlertSnackBar } from './Components/MessageBanner/AlertSnackBarHOC';
import constants from './Utils/Constants';
import { getProperName } from './Utils/Util';
import { Col, Flex } from '@amzn/stencil-react-components/layout';
import { PhoService } from './Services/PhoServices';
import {
  DEFAULT_SUPPORTED_LANGUAGES,
  getAuthPortalUrl,
  getCandidatePortalUrl,
  getSupportedLanguages,
} from './Utils/CountryConstant';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useSessionStorage } from './hooks/useSessionStorage';
import useArb from './hooks/useArb';
import LanguageModal from './Components/Modals/LanguageModal';
import { useAuth } from 'react-oidc-context';
import { HBACandidateDataService } from './Services/HBACandidateDataService';
import { getAuthConfigFromHostname } from './config/authConfig';
import { ROMPService } from './Services/ROMPService';
import { buildAuthPortalURL } from './Utils/authHelper';

function App(props: any) {
  const { t, isLoading: translationIsLoading } = useArb('appStrings');
  const [loader, setLoader] = useState(true);
  const [isOpenModal, setIsOpenModel] = useState(false);
  const [showModules, setShowModules] = useState(false);

  const [localeLocalStorage] = useLocalStorage('locale', null);
  const [, setGrievanceFeatureFlag] = useLocalStorage(constants.FEATURE_FLAGS.GRIEVANCE_REPORTING, false);
  const [, setSupportedLanguagesFeatureFlag] = useLocalStorage(
    constants.FEATURE_FLAGS.SUPPORTED_LANGUAGES,
    DEFAULT_SUPPORTED_LANGUAGES,
  );
  const [, setHBADeploymentGroup] = useSessionStorage(constants.FEATURE_FLAGS.HBA_DEPLOYMENT_GROUP, false);

  const auth = useAuth();
  const isHBAStage = getAuthConfigFromHostname() !== null;
  const useSSO = isHBAStage;

  useEffect(() => {
    setHBADeploymentGroup(isHBAStage);
  }, [isHBAStage, setHBADeploymentGroup]);

  // SSO Authentication Effect
  useEffect(() => {
    if (!useSSO) return;

    async function fetchData() {
      setLoader(true);
      if (init) await init();

      await bypassGetCandidateProfileForHBADeploymentGroup();
    }

    if (window.location.pathname !== '/') {
      sessionStorage.setItem('post_auth_path', window.location.pathname);
      sessionStorage.setItem('post_auth_params', window.location.search);
    }

    if (!auth.isAuthenticated && !auth.isLoading && !auth.error) {
      auth.signinRedirect();
      return;
    }

    if (auth.isAuthenticated) {
      const urlParams = new URLSearchParams(window.location.search || sessionStorage.getItem('post_auth_params') || '');
      const loginHint = urlParams.get('login_hint')?.split(':')[0];
      const candidateId = urlParams.get('candidateId');
      const userId = (auth.user?.profile?.identities as any[])?.[0]?.userId;
      const email = auth.user?.profile?.email;

      if (
        (loginHint && email && loginHint !== email) ||
        (candidateId && userId && candidateId !== userId) ||
        !auth?.user?.access_token
      ) {
        redirectToAuthPortal(sessionStorage.getItem('stage'));
      }

      if (auth.user?.access_token) {
        sessionStorage.setItem('pho_access_token', auth.user.access_token);
      }

      const postAuthPath = sessionStorage.getItem('post_auth_path');
      const postAuthParams = sessionStorage.getItem('post_auth_params');

      if (!translationIsLoading) {
        const pageUrl = new URL(document.URL);
        const paths = pageUrl.pathname;
        const searchParams = new URLSearchParams(window.location.search);
        const loginHintParts = searchParams.get('login_hint')?.split(':');
        const agencyId = loginHintParts && loginHintParts.length > 3 ? loginHintParts[3] : undefined;

        sessionStorage.setItem('site', paths.split('/')[1]);
        sessionStorage.setItem('role', paths.split('/')[2]);
        sessionStorage.setItem('phoId', searchParams.get('phoId') as string);
        sessionStorage.setItem('candidateId', searchParams.get('candidateId') as string);
        sessionStorage.setItem('applicationId', searchParams.get('applicationId') as string);

        if (agencyId) {
          sessionStorage.setItem('agencyId', agencyId);
        }

        reportUsername(searchParams.get('candidateId') as string);

        fetchData();
      }

      if (postAuthPath && postAuthParams && window.location.pathname === '/') {
        sessionStorage.removeItem('post_auth_path');
        sessionStorage.removeItem('post_auth_params');

        window.location.href = `${window.location.origin}${postAuthPath}${postAuthParams}`;
        return;
      }

      setShowModules(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useSSO,
    auth?.isAuthenticated,
    auth?.isLoading,
    auth?.error,
    auth,
    translationIsLoading,
    auth?.user?.access_token,
  ]);

  // Original Authentication Flow (completely untouched)
  useEffect(() => {
    if (useSSO) return;
    async function fetchData() {
      setLoader(true);
      if (init) await init();

      if (process.env.NODE_ENV === 'development' || isHBAStage) {
        await bypassGetCandidateProfileForHBADeploymentGroup();
        return;
      }

      swayamDeploymentGroup();
    }
    if (!translationIsLoading) {
      const pageUrl = new URL(document.URL);
      const paths = pageUrl.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      if (sessionStorage.getItem('idtoken') === 'null' || sessionStorage.getItem('accesstoken') === null) {
        sessionStorage.setItem('site', paths.split('/')[1]);
        sessionStorage.setItem('role', paths.split('/')[2]);
        sessionStorage.setItem('idtoken', searchParams.get('idtoken') as string);
        sessionStorage.setItem('accesstoken', searchParams.get('accesstoken') as string);
        sessionStorage.setItem('phoId', searchParams.get('phoId') as string);
        sessionStorage.setItem('candidateId', searchParams.get('candidateId') as string);
        sessionStorage.setItem('applicationId', searchParams.get('applicationId') as string);
        reportUsername(searchParams.get('candidateId') as string);
      }

      fetchData();
    }
    // eslint-disable-next-line
  }, [translationIsLoading]);

  const getAppConfigData = async () => {
    PhoService.getAppConfig()
      .then((res) => {
        const supportedLanguages = getSupportedLanguages(res?.data?.PHO_LANGUAGES);
        setSupportedLanguagesFeatureFlag(supportedLanguages);
        setGrievanceFeatureFlag(!!res?.data?.FEATURE_FLAG_ENABLE_GRIEVANCE_REPORTING);
      })
      .catch((err) => {
        /* istanbul ignore next */
        props.snackbarShowMessage(t('PHO-error-server-failed', 'Something went wrong.'), 'error', '3000');
      });
  };

  const getCandidateProfile = async (body: { email: string | null }) => {
    setLoader(true);
    CandidateService.getCandidateProfileByEmail(body)
      .then((res) => {
        const body = res && res.data;
        setLoader(false);
        sessionStorage.setItem('candidateName', getProperName(body));
        getAppConfigData();
        // Show language selector if locale has not been set
        if (!localeLocalStorage) {
          setIsOpenModel(true);
        }
        setShowModules(true);
      })
      .catch((err) => {
        setLoader(false);
        props.snackbarShowMessage(
          t('PHO-error-profile-failed', 'Something went wrong, unable to fetch profile data.'),
          'error',
          '3000',
        );
      });
  };

  const bypassGetCandidateProfileForHBADeploymentGroup = async () => {
    try {
      const candidateData = await HBACandidateDataService.getCandidateData({
        candidateId: sessionStorage.getItem('candidateId') as any,
      });

      sessionStorage.setItem(
        'candidateName',
        `${candidateData?.data?.firstName || ''} ${candidateData?.data?.lastName || ''}`.trim(),
      );

      if (sessionStorage.getItem('agencyId')) {
        const rompData = await ROMPService.getROMPData({
          organizationId: sessionStorage.getItem('agencyId') as any,
        });
        sessionStorage.setItem('agencyName', rompData?.data?.organizationName);
      }

      await getAppConfigData();

      if (!localeLocalStorage) {
        setIsOpenModel(true);
      }

      setShowModules(true);
    } catch (error) {
      console.error('Error in bypassGetCandidateProfileForHBADeploymentGroup:', error);
      props.snackbarShowMessage(
        t('PHO-error-profile-failed', 'Something went wrong, unable to fetch profile data.'),
        'error',
        '3000',
      );
    } finally {
      setLoader(false);
    }
  };

  const swayamDeploymentGroup = async () => {
    await CandidateService.verifySession()
      .then(async (data) => {
        const session = data.data.session;
        if (!session) redirectToCandidatePortal(sessionStorage.getItem('stage'));
        else {
          await getCandidateProfile({
            email: sessionStorage.getItem('candidateId'),
          });
        }
        setLoader(false);
      })
      .catch((err) => {
        console.error('Error in fetchData:', err);
        setLoader(false);
        props.snackbarShowMessage(t('PHO-error-server-failed', 'Something went wrong.'), 'error', '3000');
        window.setTimeout(() => redirectToCandidatePortal(sessionStorage.getItem('stage')), 1500);
      });
  };

  const redirectToCandidatePortal = (stage: any) => {
    props.snackbarShowMessage(
      t(
        'PHO-error-authentication-failed',
        'User session is not verified please login in candidate portal & redirect back...',
      ),
      'error',
    );
    const candidatePortalURL = getCandidatePortalUrl(stage);
    window.setTimeout(() => {
      window.location.href = candidatePortalURL;
      sessionStorage.clear();
    }, 2500);
  };

  const redirectToAuthPortal = (stage: any) => {
    const hbaAuthPortalURL = getAuthPortalUrl(stage);
    window.setTimeout(() => {
      window.location.href = buildAuthPortalURL('app', hbaAuthPortalURL);
      sessionStorage.clear();
    }, 500);
    return;
  };

  if (useSSO) {
    if (auth.isLoading) {
      return <FullPageCenteredSpinner loadingText={t('PHO-loading-user-session', 'Checking user session...')} />;
    }

    if (auth.error) {
      props.snackbarShowMessage(
        t('PHO-error-authentication-failed', 'Authentication failed. Please try again.'),
        'error',
        '3000',
      );
      return <div id="snack-bar-authentication-error">Authentication Error: {auth.error.message}</div>;
    }

    if (!auth.isAuthenticated) {
      return <FullPageCenteredSpinner loadingText={t('PHO-loading-user-session', 'Authenticating...')} />;
    }
  }

  if (loader) {
    /* istanbul ignore next */
    return <FullPageCenteredSpinner loadingText={t('PHO-loading-user-session', 'Checking user session...')} />;
  }

  return (
    <Col height="100vh" data-testid="app-container">
      <HeaderTab />
      <Flex flexGrow={1} flexDirection="row">
        {showModules && <HomePage />}
      </Flex>
      <LanguageModal
        isOpen={isOpenModal}
        shouldCloseOnClickOutside={false}
        close={() => {
          setIsOpenModel(false);
        }}
      />
    </Col>
  );
}

/* istanbul ignore next */
function reportUsername(username: string): void {
  if (username !== null && username !== '') {
    const authenticatedUserEvent = new CustomEvent('cathode-customerId', {
      detail: {
        customerId: username,
      },
    });
    document.dispatchEvent(authenticatedUserEvent);
  }
}

export default withAlertSnackBar(App);
